import { forwardRef, memo, useCallback, useMemo, type ChangeEvent } from 'react';
import { useIntl, type PrimitiveType } from 'react-intl';
// Material UI imports
import Box from '@mui/material/Box';
import Checkbox, { type CheckboxProps } from '@mui/material/Checkbox';
// Material Icon imports
import CheckBold from 'mdi-material-ui/CheckBold';
// SCSS imports
import { icon, smallIcon, inactive } from './CheckboxButton.module.scss';

type CheckboxButtonProps = {
  readonly id?: number;
  readonly label?: string;
  readonly labelValues?: Record<string, PrimitiveType>;
  readonly checked?: boolean;
  readonly onChange?: (checked: boolean, id?: number) => void;
  readonly disabled?: boolean;
  readonly small?: boolean;
  readonly className?: string;
  readonly sx?: CheckboxProps['sx'];
}

const CheckboxButton = forwardRef<HTMLButtonElement, CheckboxButtonProps>(({
  id,
  label,
  labelValues,
  checked = false,
  onChange,
  disabled = false,
  small = false,
  className,
  sx
}, ref) => {
  const { formatMessage } = useIntl();

  const clsName = `${small ? smallIcon : icon} ${disabled ? inactive : ''}`;

  const handleChange = useCallback((_event: ChangeEvent<HTMLInputElement>, chk: boolean) => {
    onChange?.(chk, id);
  }, [onChange, id]);

  const inputProps = useMemo(() => ({
    'aria-label': formatMessage({ id: label || 'common.checkbox.label', defaultMessage: label }, labelValues)
  }), [label, labelValues, formatMessage]);

  return (
    <Checkbox
        ref={ref}
        sx={sx}
        checked={checked}
        color="secondary"
        onChange={handleChange}
        inputProps={inputProps}
        disabled={disabled}
        disableRipple
        disableFocusRipple
        disableTouchRipple
        icon={<Box className={clsName}/>}
        checkedIcon={
          <Box className={clsName}>
            <CheckBold color="primary"/>
          </Box>
        }
        className={className}
    />
  );
});

CheckboxButton.displayName = 'CheckboxButton';

export default memo(CheckboxButton);
